import { z } from "zod";
import { defineStepConfig, VisibilityPredicateType } from "@erp/workflows";
import { entityOfType } from "../../../utils/system-entities";
import { IntegrationKey, SystemEntity } from "@erp/shared-types";

/**
 * Fetch Transfer Order from Famous Step
 *
 * Fetches transfer order(s) from Famous Software by order number(s) and ingests them
 * into our database as TransferOrder(s) with line items and charges.
 *
 * Famous orders with SOTYPE='9' are classified as transfer orders during ingestion.
 *
 * Input: `orderNumbers` (array of Famous order numbers) must be provided at runtime.
 *
 * Note: This is a backend-only step that requires Famous database access.
 */
export const fetchTransferOrderFromFamousStep = defineStepConfig({
  type: "fetch_transfer_order_from_famous",
  name: "Fetch TO from Famous",
  description: "Fetch and ingest transfer order(s) from Famous Software",
  category: "Integration",
  visibility: [
    [
      {
        type: VisibilityPredicateType.Integration,
        integration: IntegrationKey.Famous,
      },
    ],
  ],
  inputSchema: z.object({
    orderNumbers: z
      .array(z.string().trim())
      .describe("Array of Famous order numbers to fetch as transfer orders"),
  }),

  outputSchema: z.object({
    transferOrders: z
      .array(entityOfType(SystemEntity.IM_TRANSFER_ORDER))
      .describe("Array of Transfer Orders (created/updated)")
      .optional(),
  }),

  configSchema: z.object({
    cancelIfNotFound: z
      .boolean()
      .prefault(false)
      .describe("Cancel the workflow if no transfer orders are found"),
  }),

  emits: [] as const,
});
